// src/components/forms/GenerateTokenForm.js
import React, { useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // 2. Import useParams and useNavigate
import ParticipantBulkInsertDialog from './ParticipantBulkInsertDialog';

// Import your reusable UI components
import Button from '../ui/Button';

// Import your authenticated API hook
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';

import API_CONFIG from '../../config/api'
const BASE_URL = API_CONFIG.BASE_URL

function GenerateTokenForm({ surveyId, onTokenGenerated }) {
	// State for the form inputs
	const [tag, setTag] = useState('');
	const [description, setDescription] = useState('');
	const [validFrom, setValidFrom] = useState('');
	const [validUntil, setValidUntil] = useState('');
	const [maxUse, setMaxUse] = useState('');
	const navigate = useNavigate(); // Initialize useNavigate hook
	
	// State for API call status
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);

	// Get the authenticated fetch method from your hook
	const { authenticatedFetch } = useAuthenticatedApi();

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		// Form validation
		if (!tag.trim()) {
			setError('Tag cannot be empty.');
			return;
		}
		if (!validFrom) {
			setError('Valid from date is required.');
			return;
		}
		if (!validUntil) {
			setError('Valid until date is required.');
			return;
		}
		if (!maxUse || maxUse <= 0) {
			setError('Max use must be a positive number.');
			return;
		}
		
		// Check if validFrom is before validUntil
		if (new Date(validFrom) >= new Date(validUntil)) {
			setError('Valid from date must be before valid until date.');
			return;
		}

		setIsLoading(true);
		setError(null);
		setSuccess(null);

		try {
			// Prepare the request body
			const tokenData = {
				"survey_id": surveyId,
				"name": tag,
				"description": description,
				"valid_from": validFrom,
				"valid_until": validUntil,
				"max_use": parseInt(maxUse)
			};
			
			// Make the authenticated API call
			const url = BASE_URL + `/active_surveys/create-participant-token/${surveyId}`
			const result = await authenticatedFetch(url, { body: JSON.stringify(tokenData) }, 'POST');
			if (result.status === 404) {
				// Redirect to 404 page with a custom message
				navigate('/404', { state: { message: `Survey with ID "${surveyId}" not found in database.` } });
				return;
			}
			if (!result.ok) {
				// Handle other non-2xx status codes (e.g., 500, 401, 403)
				let errorMessage = `Failed to fetch survey: Server responded with status ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
					// Ignore if response body isn't JSON
				}
				console.error("GenerateTokenForm API Error:", errorMessage, result);
				setError(errorMessage);
				return; // Stop further execution
			}
			const datas = await result.json();

			if (datas) {
				setSuccess('Access token generated successfully!');
			} else {
				console.error("API returned empty or invalid data structure:", datas);
				setError("Error API returned empty or invalid data structure.");
			}
			// Reset form on success or error
			setTag('');
			setDescription('');
			setValidFrom('');
			setValidUntil('');
			setMaxUse('');
			
		} catch (err) {
			setError(err.detail || 'Failed to generate access token. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div>
			<div className="mb-6">
			<h2 className="text-xl font-semibold text-primary mb-2 flex items-center justify-between">
				Generate Access Token
				<ParticipantBulkInsertDialog surveyId={surveyId} />
			</h2>
				<p className="text-sm text-gray-600">
					Create a new access token to control who can access your survey.
				</p>
			</div>

			<form onSubmit={handleSubmit} className="space-y-6">
				<div className="grid gap-6">
					{/* Tag Field */}
					<div className="grid gap-2">
						<label htmlFor="tag" className="font-semibold text-primary">
							Tag
						</label>
						<input
							id="tag"
							value={tag}
							onChange={(e) => setTag(e.target.value)}
							placeholder="e.g., beta-testers, marketing-team"
							className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
							disabled={isLoading}
						/>
					</div>

					{/* Description Field */}
					<div className="grid gap-2">
						<label htmlFor="description" className="font-semibold text-primary">
							Description
						</label>
						<textarea
							id="description"
							rows={3}
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="Describe who this token is for and its purpose..."
							className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
							disabled={isLoading}
						/>
					</div>

					{/* Date Fields Row */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="grid gap-2">
							<label htmlFor="validFrom" className="font-semibold text-primary">
								Valid From
							</label>
							<input
								id="validFrom"
								type="datetime-local"
								value={validFrom}
								onChange={(e) => setValidFrom(e.target.value)}
								className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
								disabled={isLoading}
							/>
						</div>

						<div className="grid gap-2">
							<label htmlFor="validUntil" className="font-semibold text-primary">
								Valid Until
							</label>
							<input
								id="validUntil"
								type="datetime-local"
								value={validUntil}
								onChange={(e) => setValidUntil(e.target.value)}
								className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
								disabled={isLoading}
							/>
						</div>
					</div>

					{/* Max Use Field */}
					<div className="grid gap-2">
						<label htmlFor="maxUse" className="font-semibold text-primary">
							Maximum Uses
						</label>
						<input
							id="maxUse"
							type="number"
							min="1"
							value={maxUse}
							onChange={(e) => setMaxUse(e.target.value)}
							placeholder="e.g., 100"
							className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
							disabled={isLoading}
						/>
					</div>
					
					{/* Error Message */}
					{error && <p className="text-center text-sm text-red-500">{error}</p>}
					
					{/* Success Message */}
					{success && <p className="text-center text-sm text-green-500">{success}</p>}
				</div>

				{/* Submit Button */}
				<div className="flex justify-end pt-4">
					<Button type="submit" disabled={isLoading || !tag.trim()}>
						{isLoading && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
						Generate Token
					</Button>
				</div>
			</form>
		</div>
	);
}

export default GenerateTokenForm;